import React, { useState, useCallback } from 'react';
import {
  useCreateDocumentTag,
  useUpdateDocumentTag,
  useDeleteDocumentTag,
  DocumentTag,
  DocumentTagCreate,
} from './react-query/useDocumentTags';

interface TagFormData {
  name: string;
  description: string;
  color: string;
  icon: string;
}

interface UseDocumentTagActionsReturn {
  // Form state
  formData: TagFormData;
  setFormData: React.Dispatch<React.SetStateAction<TagFormData>>;
  resetForm: () => void;

  // Dialog state
  isCreateOpen: boolean;
  editingTag: DocumentTag | null;
  deletingTag: DocumentTag | null;

  // Dialog actions
  openCreateDialog: () => void;
  closeCreateDialog: () => void;
  openEditDialog: (tag: DocumentTag) => void;
  closeEditDialog: () => void;
  openDeleteDialog: (tag: DocumentTag) => void;
  closeDeleteDialog: () => void;

  // Mutation actions
  handleCreate: () => Promise<void>;
  handleUpdate: () => Promise<void>;
  handleDelete: () => Promise<void>;

  // Mutation states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

const DEFAULT_FORM_DATA: TagFormData = {
  name: '',
  description: '',
  color: '#3B82F6',
  icon: 'tag',
};

/**
 * Custom hook for managing document tag CRUD operations
 * Handles form state, dialog state, and mutation actions with proper error handling
 */
export function useDocumentTagActions(): UseDocumentTagActionsReturn {
  // Form state
  const [formData, setFormData] = useState<TagFormData>(DEFAULT_FORM_DATA);

  // Dialog state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<DocumentTag | null>(null);
  const [deletingTag, setDeletingTag] = useState<DocumentTag | null>(null);

  // Mutations (already handle toasts internally via useApiMutation)
  const createMutation = useCreateDocumentTag();
  const updateMutation = useUpdateDocumentTag();
  const deleteMutation = useDeleteDocumentTag();

  // Reset form to default values
  const resetForm = useCallback(() => {
    setFormData(DEFAULT_FORM_DATA);
  }, []);

  // Dialog actions
  const openCreateDialog = useCallback(() => {
    setIsCreateOpen(true);
  }, []);

  const closeCreateDialog = useCallback(() => {
    setIsCreateOpen(false);
    resetForm();
  }, [resetForm]);

  const openEditDialog = useCallback((tag: DocumentTag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description || '',
      color: tag.color || '#3B82F6',
      icon: tag.icon || 'tag',
    });
  }, []);

  const closeEditDialog = useCallback(() => {
    setEditingTag(null);
    resetForm();
  }, [resetForm]);

  const openDeleteDialog = useCallback((tag: DocumentTag) => {
    setDeletingTag(tag);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeletingTag(null);
  }, []);

  // Mutation actions
  const handleCreate = useCallback(async () => {
    const createData: DocumentTagCreate = {
      name: formData.name,
      description: formData.description || undefined,
      color: formData.color,
      icon: formData.icon,
    };

    await createMutation.mutateAsync(createData);
    closeCreateDialog();
  }, [formData, createMutation, closeCreateDialog]);

  const handleUpdate = useCallback(async () => {
    if (!editingTag) return;

    await updateMutation.mutateAsync({
      id: editingTag.id,
      data: {
        name: formData.name,
        description: formData.description || undefined,
        color: formData.color,
        icon: formData.icon,
      },
    });
    closeEditDialog();
  }, [editingTag, formData, updateMutation, closeEditDialog]);

  const handleDelete = useCallback(async () => {
    if (!deletingTag) return;

    await deleteMutation.mutateAsync(deletingTag.id);
    closeDeleteDialog();
  }, [deletingTag, deleteMutation, closeDeleteDialog]);

  return {
    // Form state
    formData,
    setFormData,
    resetForm,

    // Dialog state
    isCreateOpen,
    editingTag,
    deletingTag,

    // Dialog actions
    openCreateDialog,
    closeCreateDialog,
    openEditDialog,
    closeEditDialog,
    openDeleteDialog,
    closeDeleteDialog,

    // Mutation actions
    handleCreate,
    handleUpdate,
    handleDelete,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
